import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate'
import AspectRatio from '@mui/icons-material/AspectRatio'
import BrokenImage from '@mui/icons-material/BrokenImage'
import Edit from '@mui/icons-material/Edit'

import { Badge, Grow } from '@mui/material'
import { useMemo } from 'react'
import { IndeterminateLoading } from 'src/consumer/components/ConsumerProfilePage/ProfileLeftPannelStyle'
import { mediaURL } from 'src/consumer/services'
import {
  AvatarActionIcon,
  AvatarRemoveIcon,
} from 'src/mentor/components/MentorProfilePage/MentorProfilePageStyles'
import {
  CustomImageAvatar,
  CustomImageAvatarButton,
  CustomImageAvatarContainer,
  CustomImageAvatarFallBack,
} from 'src/consumer/components/helperComponentsStyles'
import { useFilePondUpload } from './useFilePondUpload'
import Close from '@mui/icons-material/Close'

export default function PictureUploadComponent({
  value,
  onChange,
  aspectHeight,
  aspectWidth,
  showRemoveButton,
  title,
  id = 'imageId',
  hideRecommendedText = false,
  disabled,
  ...rest
}) {
  const cssAspectRatio = useMemo(() => {
    if (!aspectHeight && !aspectWidth) {
      return undefined
    }
    return aspectWidth && aspectHeight && aspectWidth / aspectHeight
  }, [aspectHeight, aspectWidth])

  const { triggerUpload, isUploading, FilePondComponent, localPreviewUrl } = useFilePondUpload({
    aspectWidth,
    aspectHeight,
    onUploadComplete: (imageId) => {
      onChange({
        target: { value: imageId, id },
      })
    },
  })

  // Phase 14j — prefer the just-uploaded blob URL while the media CDN is propagating the new
  // file to its edges (typically a few seconds). Falls through to the CDN URL once the page
  // remounts or the consumer hydrates the value from a server fetch on a subsequent visit.
  // Without this, freshly-uploaded images rendered as a BrokenImage placeholder until the user
  // re-uploaded — the file existed, the CDN just hadn't caught up yet.
  const renderedSrc = localPreviewUrl || (value ? `${mediaURL}${value}` : null)

  const handleClick = () => {
    if (!disabled) {
      triggerUpload()
    }
  }

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ gap: '18px', paddingTop: '30px' }}
    >
      <CustomImageAvatarButton
        onClick={handleClick}
        disabled={disabled}
        sx={{ p: 0, position: 'relative' }}
      >
        {showRemoveButton && (
          <AvatarRemoveIcon
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onChange({
                target: { value: null, id },
              })
            }}
            sx={{ position: 'absolute', top: '-30px', right: '-30px', zIndex: 30 }}
          >
            <Close color="error" />
          </AvatarRemoveIcon>
        )}
        <Badge
          overlap="rectangular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <>
              <Grow in={!disabled}>
                <AvatarActionIcon imgIsThere={value}>
                  {value ? <Edit /> : <AddPhotoAlternate />}
                </AvatarActionIcon>
              </Grow>
            </>
          }
        >
          <CustomImageAvatarContainer aspectRatio={cssAspectRatio}>
            {(value || renderedSrc) && (
              <CustomImageAvatar src={renderedSrc}>
                <BrokenImage sx={{ fontSize: { xs: 40, md: 50 } }} />
              </CustomImageAvatar>
            )}
            {!value && !renderedSrc && (
              <CustomImageAvatarFallBack>
                <AspectRatio />
                {!!hideRecommendedText ? (
                  'Upload Image'
                ) : (
                  <>
                    Recommended <br /> {aspectWidth} x {aspectHeight}
                  </>
                )}
              </CustomImageAvatarFallBack>
            )}
          </CustomImageAvatarContainer>
          {isUploading && <IndeterminateLoading />}
        </Badge>
      </CustomImageAvatarButton>
      <FilePondComponent />
      <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 600 }}>{title}</div>
    </div>
  )
}
